import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';

const ReceivedProposals = () => {
  const [bidItems, setBidItems] = useState([]);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [currentBidItemId, setCurrentBidItemId] = useState(null);
  const [formData, setFormData] = useState({
    price: '',
    fabric: '',
    price_breakdown: '',
    comments: '',
    duration: ''
  });
  const [priceError, setPriceError] = useState(false);
  const [messages, setMessages] = useState([]);
  const [slideIndices, setSlideIndices] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for submission status
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);

  useEffect(() => {
    const fetchBidItems = async () => {
      try {
        const response = await axios.get('https://vasstrinfo.com//api/bidding_orders/', {
          headers: {
            'Authorization': `Bearer ${authToken.access}`,
          },
        });
        const items = Array.isArray(response.data.bids) ? response.data.bids : [];
        setBidItems(items);

        const indices = {};
        items.forEach((item) => {
          indices[item.id] = 1;
        });
        setSlideIndices(indices);
      } catch (error) {
        console.error('Error fetching bid items:', error);
        Swal.fire('Error', 'Failed to fetch bid items. Please try again later.', 'error');
      }
    };

    fetchBidItems();

    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    if (message) {
      setMessages([message]);
    }
  }, [authToken]);

  const showSlides = (n, slideGroup) => {
    const slides = document.querySelectorAll(`#slideshow-${slideGroup} img`);
    const dots = document.querySelectorAll(`#dot-container-${slideGroup} .dot`);
    if (n > slides.length) { n = 1; }
    if (n < 1) { n = slides.length; }
    slides.forEach(slide => slide.style.display = "none");
    if (slides[n - 1]) slides[n - 1].style.display = "block";
    dots.forEach(dot => dot.classList.remove("active"));
    if (dots[n - 1]) dots[n - 1].classList.add("active");
    setSlideIndices(prev => ({ ...prev, [slideGroup]: n }));
  };

  const plusSlides = (n, slideGroup) => {
    showSlides(slideIndices[slideGroup] + n, slideGroup);
  };

  const currentSlide = (n, slideGroup) => {
    showSlides(n, slideGroup);
  };

  const validatePrice = () => {
    const pricePattern = /^\d+(\.\d{1,2})?$/;
    const isValid = pricePattern.test(formData.price);
    setPriceError(!isValid);
    return isValid;
  };

  const openAcceptForm = (itemId) => {
    setCurrentBidItemId(itemId);
    setShowAcceptForm(true);
  };

  const closeOverlay = () => {
    setShowAcceptForm(false);
    setFormData({
      price: '',
      fabric: '',
      price_breakdown: '',
      comments: '',
      duration: ''
    });
    setPriceError(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'price') {
      const pricePattern = /^\d+(\.\d{1,2})?$/;
      setPriceError(!pricePattern.test(value));
    }
  };

  const handleAcceptSubmit = async (e) => {
    e.preventDefault();
    if (!validatePrice()) return;
    if (!formData.fabric || !formData.comments || !formData.duration) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }

    setIsSubmitting(true); // Disable the submit button
    try {
      const response = await axios.post(
        `https://vasstrinfo.com//api/accepted_bid/${currentBidItemId}/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${authToken.access}`,
          },
        }
      );
      const updatedItems = bidItems.map(item =>
        item.id === currentBidItemId
          ? { ...item, is_accepted: true, bid_price: formData.price }
          : item
      );
      setBidItems(updatedItems);
      closeOverlay();
      setIsSubmitting(false); // Re-enable the submit button after popup
      Swal.fire('Success', response.data.message || 'Your quote has been submitted!', 'success');
    } catch (error) {
      setIsSubmitting(false); // Re-enable the submit button after popup
      Swal.fire('Error', error.response?.data?.error || 'Failed to submit your quote. Please try again.', 'error');
    }
  };

  const handleDecline = async (itemId, e) => {
    e.preventDefault();
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to undo this action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, decline it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(
            `https://vasstrinfo.com//api/decline_bid/${itemId}/`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${authToken.access}`,
              },
            }
          );
          const updatedItems = bidItems.filter(item => item.id !== itemId);
          setBidItems(updatedItems);
          Swal.fire('Declined!', 'The proposal has been declined.', 'success');
        } catch (error) {
          Swal.fire('Error', 'Failed to decline the proposal.', 'error');
        }
      }
    });
  };

  const getImgUrl = (img) =>
    img?.startsWith('http') ? img : `https://vasstrinfo.com${img}`;

  return (
    <Base Content={
      <div className="min-h-screen">
        {/* Heading */}
        <div className="heading py-4 px-2 sm:px-4">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-orange-600 mb-1">
            Proposals Received From Customers
          </h1>
          <div className="flex items-center justify-around w-full h-8 relative">
            <div className="line left-line w-2/5 h-px bg-black"></div>
            <div className="circles flex gap-1 sm:gap-2 md:gap-3 items-center justify-center">
              {[...Array(3)].map((_, i) => (
                <div key={`smaller-${i}`} className="circle w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 rounded-full bg-black"></div>
              ))}
              <div className="circle w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full bg-black"></div>
              <div className="circle w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded-full bg-black"></div>
              <div className="circle w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full bg-black"></div>
              {[...Array(3)].map((_, i) => (
                <div key={`smaller2-${i}`} className="circle w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 rounded-full bg-black"></div>
              ))}
            </div>
            <div className="line right-line w-2/5 h-px bg-black"></div>
          </div>
        </div>

        {/* Messages */}
        {messages.length > 0 && (
          <div className="text-center px-2">
            {messages.map((message, index) => (
              <p key={index} className="message text-orange-600 text-base sm:text-lg md:text-xl my-2">✓ {message}</p>
            ))}
          </div>
        )}

        {/* Accept Form Overlay */}
        {showAcceptForm && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 overflow-y-auto flex items-center justify-center p-2 sm:p-4">
            <div className="relative bg-white rounded-lg p-4 sm:p-6 w-full max-w-md sm:max-w-xl md:max-w-2xl">
              <button 
                onClick={closeOverlay}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
              
              <form onSubmit={handleAcceptSubmit} className="space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm text-blue-600">
                  Note that you can negotiate on fabric, customisations & delivery time!
                </p>

                <div>
                  <label htmlFor="price" className="block mb-1 text-sm sm:text-base">
                    How much price would you quote for this? <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    placeholder="eg. ₹.1000"
                    pattern="^\d+(\.\d{1,2})?$"
                    title="Enter a valid price (e.g. ₹.100 or ₹.100.50)"
                    className="w-full p-2 border rounded text-sm sm:text-base"
                  />
                  {priceError && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1">
                      Enter valid price (e.g. 100 or 100.50)
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="fabric" className="block mb-1 text-sm sm:text-base">
                    Fabric this can be made of: <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="fabric"
                    name="fabric"
                    value={formData.fabric}
                    onChange={handleInputChange}
                    rows="1"
                    placeholder="eg. silk"
                    required
                    className="w-full p-2 border rounded text-sm sm:text-base"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="price_breakdown" className="block mb-1 text-sm sm:text-base">
                    Price breakdown:
                  </label>
                  <textarea
                    id="price_breakdown"
                    name="price_breakdown"
                    value={formData.price_breakdown}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="eg. Cloth cost => ₹.300, Making cost => ₹.500, Profit share => ₹.200"
                    className="w-full p-2 border rounded text-sm sm:text-base"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="comments" className="block mb-1 text-sm sm:text-base">
                    Comments: <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="comments"
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="eg. the print might vary slightly"
                    required
                    className="w-full p-2 border rounded text-sm sm:text-base"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="duration" className="block mb-1 text-sm sm:text-base">
                    How long will it take to make this outfit? <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="duration"
                    id="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded text-sm sm:text-base"
                  >
                    <option value="" disabled>Select days</option>
                    {[...Array(28)].map((_, i) => (
                      <option key={i+1} value={i+1}>{i+1} day{i+1 !== 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  className={`bg-orange-600 text-white px-4 sm:px-6 py-2 rounded-full mx-auto block hover:shadow-lg transition text-sm sm:text-base ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Bid Items Container */}
        {bidItems.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500 text-lg">No proposals received yet</p>
          </div>
        ) : (
          <div className="container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-2 sm:p-4">
            {bidItems.map(item => (
              <div key={item.id} className="card bg-white rounded-lg shadow-md overflow-hidden hover:translate-y-[-3px] transition">
                <div className="card-body flex flex-col h-full">
                  {/* Image Container */}
                  <div className="img-ctn w-full p-2">
                    <div className="slideshow-container relative h-48 sm:h-56 md:h-64 bg-gray-100" id={`slideshow-${item.id}`}>
                      {item.img1 && (
                        <img 
                          src={getImgUrl(item.img1)} 
                          alt="Product" 
                          className={`w-full h-full object-contain rounded ${slideIndices[item.id] === 1 ? 'block' : 'hidden'}`}
                        />
                      )}
                      {item.img2 && (
                        <img 
                          src={getImgUrl(item.img2)} 
                          alt="Product" 
                          className={`w-full h-full object-contain rounded ${slideIndices[item.id] === 2 ? 'block' : 'hidden'}`}
                        />
                      )}
                      {item.img1 && item.img2 && (
                        <>
                          <button 
                            className="prev absolute top-1/2 left-0 transform -translate-y-1/2 bg-black bg-opacity-30 text-white p-1 sm:p-2 rounded-r text-sm sm:text-base"
                            onClick={() => plusSlides(-1, item.id)}
                          >
                            ❮
                          </button>
                          <button 
                            className="next absolute top-1/2 right-0 transform -translate-y-1/2 bg-black bg-opacity-30 text-white p-1 sm:p-2 rounded-l text-sm sm:text-base"
                            onClick={() => plusSlides(1, item.id)}
                          >
                            ❯
                          </button>
                        </>
                      )}
                    </div>
                    {item.img1 && item.img2 && (
                      <div className="dot-container text-center py-1" id={`dot-container-${item.id}`}>
                        <span 
                          className={`dot inline-block w-2 h-2 rounded-full mx-1 cursor-pointer ${slideIndices[item.id] === 1 ? 'bg-gray-700' : 'bg-gray-300'}`}
                          onClick={() => currentSlide(1, item.id)}
                        ></span>
                        <span 
                          className={`dot inline-block w-2 h-2 rounded-full mx-1 cursor-pointer ${slideIndices[item.id] === 2 ? 'bg-gray-700' : 'bg-gray-300'}`}
                          onClick={() => currentSlide(2, item.id)}
                        ></span>
                      </div>
                    )}
                  </div>
                  {/* Content Container */}
                  <div className="card-content w-full p-2 sm:p-3 overflow-y-auto">
                    <h2 className="text-base sm:text-lg font-bold text-orange-600 mb-1">{item.category}</h2>
                    <p className="text-xs sm:text-sm"><span className="font-semibold">Measurements:</span> {item.size}</p>
                    {item.fabric && (
                      <p className="text-xs sm:text-sm"><span className="font-semibold">Requested fabric:</span> {item.fabric}</p>
                    )}
                    <p className="text-xs sm:text-sm"><span className="font-semibold">Customizations:</span> {item.customisations}</p>
                    <p className="text-xs sm:text-sm"><span className="font-semibold">Comments:</span> {item.comments}</p>
                    <p className="text-xs sm:text-sm"><span className="font-semibold">Delivery Expectation:</span> {item.duration}</p>
                    {!item.is_accepted ? (
                      <div className="btn-ctn mt-3 flex flex-wrap gap-2">
                        <button 
                          onClick={() => openAcceptForm(item.id)}
                          className="btn bg-green-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm hover:translate-y-[-3px] transition"
                        >
                          Accept
                        </button>
                        <button 
                          onClick={(e) => handleDecline(item.id, e)}
                          className="btn bg-orange-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm hover:translate-y-[-3px] transition"
                        >
                          Decline
                        </button>
                      </div>
                    ) : (
                      <div className="text-green-600 font-bold mt-2 text-sm sm:text-base">
                        Quote Given: ₹{item.bid_price}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Spacer */}
        <div className="h-16 w-full"></div>
      </div>
    }/>
  );
};

export default ReceivedProposals;